import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MathfieldElement } from "mathlive";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

const QUESTIONS = [
  {
    "id": "q1",
    "instruction": "A cylinder has radius 3 cm and height 5 cm.\nFind its volume.",
    "radius": 3,
    "height": 5,
    "radiusLabel": "3 cm",
    "heightLabel": "5 cm",
    "unit": "cm³",
    "answers": [
      "141.39"
    ],
    "displayAnswer": "141.39",
    "calculator": "yes",
    "formula": true,
    "pi": "\\pi=3.142",
    "exactVolume": 141.39,
    "appearance": {
      "orientation": "upright",
      "radius": 82,
      "bodyHeight": 133,
      "colour": "teal",
      "angle": 11
    }
  },
  {
    "id": "q2",
    "instruction": "A cylindrical tin has diameter 8 cm and height 12 cm.\nFind its volume.",
    "radius": 4,
    "height": 12,
    "diameterLabel": "8 cm",
    "heightLabel": "12 cm",
    "unit": "cm³",
    "answers": [
      "603.264"
    ],
    "displayAnswer": "603.264",
    "calculator": "yes",
    "formula": true,
    "pi": "\\pi=3.142",
    "exactVolume": 603.264,
    "appearance": {
      "orientation": "horizontal",
      "radius": 65,
      "bodyHeight": 195,
      "colour": "blue",
      "angle": 11
    }
  },
  {
    "id": "q3",
    "instruction": "A cylindrical container is shown below. Its radius is 2.5 cm and its height is 8 cm.\nFind its volume.",
    "radius": 2.5,
    "height": 8,
    "radiusLabel": "2.5 cm",
    "heightLabel": "8 cm",
    "unit": "cm³",
    "answers": [
      "157.1"
    ],
    "displayAnswer": "157.1",
    "calculator": "yes",
    "pi": "\\pi=3.142",
    "exactVolume": 157.1,
    "appearance": {
      "orientation": "tilted",
      "radius": 74,
      "bodyHeight": 125,
      "colour": "violet",
      "angle": 11
    }
  },
  {
    "id": "q4",
    "instruction": "A cylinder has radius 7 cm and height 5 cm.\nWork out its exact volume without a calculator. Use the value of π shown below.",
    "radius": 7,
    "height": 5,
    "radiusLabel": "7 cm",
    "heightLabel": "5 cm",
    "pi": "\\pi=\\frac{22}{7}",
    "unit": "cm³",
    "answers": [
      "770"
    ],
    "displayAnswer": "770",
    "calculator": "no",
    "appearance": {
      "orientation": "upright",
      "radius": 108,
      "bodyHeight": 84,
      "colour": "rose",
      "angle": 11
    }
  },
  {
    "id": "q5",
    "instruction": "A cylindrical tube has diameter 6 cm and height 14 cm.\nWork out its exact volume without a calculator. Use the value of π shown below.",
    "radius": 3,
    "height": 14,
    "diameterLabel": "6 cm",
    "heightLabel": "14 cm",
    "pi": "\\pi=\\frac{22}{7}",
    "unit": "cm³",
    "answers": [
      "396"
    ],
    "displayAnswer": "396",
    "calculator": "no",
    "appearance": {
      "orientation": "horizontal",
      "radius": 43,
      "bodyHeight": 220,
      "colour": "amber",
      "angle": 11
    }
  },
  {
    "id": "q6",
    "instruction": "A farm needs to fill a cylindrical water tank. Its internal diameter is 1.2 m and its internal height is 150 cm.\nHow many litres of water will fill the tank? Give your answer to the nearest litre.\n1 m³ = 1000 litres.",
    "diameterLabel": "1.2 m",
    "heightLabel": "150 cm",
    "unit": "litres",
    "answers": [
      "1696"
    ],
    "displayAnswer": "1696\\text{ or }1697",
    "calculator": "yes",
    "range": [
      1696,
      1697
    ],
    "appearance": {
      "orientation": "upright",
      "radius": 76,
      "bodyHeight": 185,
      "colour": "blue",
      "angle": 11
    }
  },
  {
    "id": "q7",
    "instruction": "A cylindrical water butt has internal diameter 80 cm and internal height 120 cm. It is 75% full.\nHow many litres of water are in the butt? Give your answer to the nearest litre.\n1000 cm³ = 1 litre.",
    "diameterLabel": "80 cm",
    "heightLabel": "120 cm",
    "fill": 0.75,
    "unit": "litres",
    "answers": [
      "452"
    ],
    "displayAnswer": "452\\text{ or }453",
    "calculator": "yes",
    "range": [
      452,
      453
    ],
    "appearance": {
      "orientation": "upright",
      "radius": 84,
      "bodyHeight": 165,
      "colour": "green",
      "angle": 11
    }
  },
  {
    "id": "q8",
    "instruction": "A café has a full cylindrical drinks dispenser with internal diameter 60 cm and internal height 80 cm. The drink is poured into 2-litre bottles.\nWhat is the greatest number of completely full bottles it can fill? Ignore spillage.\n1000 cm³ = 1 litre.",
    "diameterLabel": "60 cm",
    "heightLabel": "80 cm",
    "unit": "bottles",
    "answers": [
      "113"
    ],
    "displayAnswer": "113",
    "calculator": "yes",
    "range": [
      113,
      113
    ],
    "appearance": {
      "orientation": "tilted",
      "radius": 65,
      "bodyHeight": 145,
      "colour": "amber",
      "angle": -12
    }
  },
  {
    "id": "q9",
    "instruction": "A gardener wants to fill a circular raised bed with soil to a depth of 35 cm. The bed has an internal diameter of 2.4 m.\nSoil is sold in 50-litre bags at £6.75 per bag.\nComplete the table to show the volume of soil, the number of whole bags to buy and the total cost. Give the volume to 3 decimal places.\n1 m³ = 1000 litres.",
    "diameterLabel": "2.4 m",
    "heightLabel": "35 cm",
    "calculator": "yes",
    "fields": [
      {
        "key": "volume",
        "label": "Volume of soil",
        "expected": 1.583,
        "unit": "m³",
        "range": [
          1.583,
          1.584
        ]
      },
      {
        "key": "bags",
        "label": "Whole bags needed",
        "expected": 32,
        "unit": "bags"
      },
      {
        "key": "cost",
        "label": "Total cost",
        "expected": 216,
        "unit": "£"
      }
    ],
    "displayAnswer": "Volume: 1.583–1.584 m³; bags: 32; cost: £216.00",
    "appearance": {
      "orientation": "upright",
      "radius": 112,
      "bodyHeight": 65,
      "colour": "earth",
      "angle": 11
    }
  },
  {
    "id": "q10",
    "instruction": "A charity is making 16 cylindrical candles. Each candle has a diameter of 6 cm and a height of 10 cm.\nEach 1 cm³ of wax has a mass of 0.9 g. Wax is sold in 500 g bags.\nComplete the order form to buy enough wax at the lowest cost. Enter 0 for unused packs and their costs.",
    "diameterLabel": "6 cm",
    "heightLabel": "10 cm",
    "diagramCaption": "One candle — not to scale",
    "calculator": "yes",
    "orderTitle": "Wax order form",
    "packOffers": [
      [
        1,
        "8.50"
      ],
      [
        5,
        "38.00"
      ],
      [
        10,
        "70.00"
      ],
      [
        20,
        "132.00"
      ]
    ],
    "fields": [
      {
        "key": "bags",
        "label": "Minimum whole bags needed",
        "expected": 9,
        "unit": "bags"
      },
      {
        "key": "pack1",
        "label": "Packs of 1",
        "expected": 0,
        "unit": ""
      },
      {
        "key": "cost1",
        "label": "Cost of packs of 1",
        "expected": 0,
        "unit": "£"
      },
      {
        "key": "pack5",
        "label": "Packs of 5",
        "expected": 0,
        "unit": ""
      },
      {
        "key": "cost5",
        "label": "Cost of packs of 5",
        "expected": 0,
        "unit": "£"
      },
      {
        "key": "pack10",
        "label": "Packs of 10",
        "expected": 1,
        "unit": ""
      },
      {
        "key": "cost10",
        "label": "Cost of packs of 10",
        "expected": 70,
        "unit": "£"
      },
      {
        "key": "pack20",
        "label": "Packs of 20",
        "expected": 0,
        "unit": ""
      },
      {
        "key": "cost20",
        "label": "Cost of packs of 20",
        "expected": 0,
        "unit": "£"
      },
      {
        "key": "total",
        "label": "Total order cost",
        "expected": 70,
        "unit": "£"
      }
    ],
    "displayAnswer": "9 bags needed; buy 1 pack of 10 bags for £70.00. All other packs and costs: 0.",
    "appearance": {
      "orientation": "upright",
      "radius": 65,
      "bodyHeight": 178,
      "colour": "violet",
      "angle": 11
    }
  }
];

const STORAGE_KEY = "volume_of_cylinders_quiz_v3";
const SUBMIT_URL =
  `${process.env.REACT_APP_HOMEWORK_API_URL || (process.env.NODE_ENV === "development" ? "http://localhost:5001" : "")}/api/homework/volume-of-cylinders`;

function normalize(raw) {
  let value = String(raw ?? "")
    .toLowerCase()
    .replace(/\\left|\\right/g, "")
    .replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, "$1/$2")
    .replace(/\\cdot|\\times/g, "*")
    .replace(/\\div/g, "/")
    .replace(/\\,/g, "")
    .replace(/\s+/g, "")
    .replace(/[{}()]/g, "");

  return value.replace(/\*/g, "");
}

function makeSubmissionId() {
  if (
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function ResultIcon({ type, size = 28 }) {
  const isCorrect = type === "correct";
  const colour = isCorrect ? "#138a5b" : "#d64545";
  const paleColour = isCorrect ? "#e7f7ef" : "#fdecec";

  return (
    <svg
      aria-label={isCorrect ? "Correct" : "Incorrect"}
      role="img"
      width={size}
      height={size}
      viewBox="0 0 32 32"
      style={{ display: "inline-block", flexShrink: 0 }}
    >
      <circle cx="16" cy="16" r="14" fill={paleColour} stroke={colour} strokeWidth="2" />
      {isCorrect ? (
        <path
          d="M9.2 16.2l4.3 4.4 9.5-10"
          fill="none"
          stroke={colour}
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M11 11l10 10M21 11L11 21"
          fill="none"
          stroke={colour}
          strokeWidth="3.2"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

export default function VolumeOfCylindersQuiz() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Volume of Cylinders';
    return () => { document.title = previousTitle; };
  }, []);

  const navigate = useNavigate();
  const questions = useMemo(() => QUESTIONS, []);

  const [studentName, setStudentName] = useState("");
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const [current, setCurrent] = useState(0);
  const [studentAnswer, setStudentAnswer] = useState("");
  const [working, setWorking] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [questionResults, setQuestionResults] = useState(Array(questions.length).fill(null));
  const [studentAnswers, setStudentAnswers] = useState(Array(questions.length).fill(""));
  const [workingAnswers, setWorkingAnswers] = useState(Array(questions.length).fill(""));
  const [attemptHistory, setAttemptHistory] = useState(Array.from({ length: questions.length }, () => []));
  const [optionalTableProgress, setOptionalTableProgress] = useState({});
  const [checkedTableProgress, setCheckedTableProgress] = useState({});
  const [reviewMode, setReviewMode] = useState(false);
  const [done, setDone] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState("idle");
  const [submissionError, setSubmissionError] = useState("");
  const [submissionId, setSubmissionId] = useState(makeSubmissionId);

  useEffect(() => {
    if (!customElements.get("math-field")) customElements.define("math-field", MathfieldElement);
  }, []);

  useEffect(() => {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (!data) return;
      setStudentName(data.studentName || "");
      setNameConfirmed(Boolean(data.nameConfirmed));
      setCurrent(Number.isInteger(data.current) ? data.current : 0);
      if (Array.isArray(data.questionResults) && data.questionResults.length === questions.length) setQuestionResults(data.questionResults);
      if (Array.isArray(data.studentAnswers) && data.studentAnswers.length === questions.length) setStudentAnswers(data.studentAnswers);
      if (Array.isArray(data.workingAnswers) && data.workingAnswers.length === questions.length) setWorkingAnswers(data.workingAnswers);
      if (Array.isArray(data.attemptHistory) && data.attemptHistory.length === questions.length) setAttemptHistory(data.attemptHistory);
      setOptionalTableProgress(data.optionalTableProgress || {});
      setCheckedTableProgress(data.checkedTableProgress || {});
      setDone(Boolean(data.done));
      setSubmissionStatus(data.submissionStatus || "idle");
      setSubmissionError(data.submissionError || "");
      setSubmissionId(data.submissionId || makeSubmissionId());
      const index = Number.isInteger(data.current) ? data.current : 0;
      setStudentAnswer(data.studentAnswers?.[index] || "");
      setAttempts(data.attemptHistory?.[index]?.length || 0);
      setWorking(data.workingAnswers?.[index] || "");
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [questions.length]);

  const saveProgress = (override = {}) => {
    const payload = {
      studentName, nameConfirmed, current, questionResults, studentAnswers,
      workingAnswers, attemptHistory, optionalTableProgress, checkedTableProgress, done, submissionStatus, submissionError, submissionId, ...override,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  };

  const confirmName = (event) => {
    event.preventDefault();
    const cleanName = studentName.trim();
    if (!cleanName) return;
    setStudentName(cleanName);
    setNameConfirmed(true);
    saveProgress({ studentName: cleanName, nameConfirmed: true });
  };

  const checkAnswer = () => {
    const q = questions[current];
    const values=optionalTableProgress[q.id]||{};
    if(q.fields?q.fields.some(f=>String(values[f.key]??'').trim()===''):!studentAnswer.trim()){
      setFeedback(q.fields?'Please complete every box in the table, including 0 for unused packs.':'Please enter an answer first.');return;
    }
    const entered=q.fields?fieldSummary(q,values):studentAnswer;
    const updatedChecked=q.fields?{...checkedTableProgress,[q.id]:{...values}}:checkedTableProgress;
    if(q.fields)setCheckedTableProgress(updatedChecked);
    const isCorrect=q.fields?q.fields.every(f=>matches(values[f.key],f.expected,f.range)):matchesCylinderAnswer(q,entered);
    void playQuizFeedback(isCorrect);
    const updatedStudentAnswers = [...studentAnswers];
    const updatedWorkingAnswers = [...workingAnswers];
    const updatedHistory = attemptHistory.map((history) => [...history]);
    updatedStudentAnswers[current] = entered;
    updatedWorkingAnswers[current] = working;
    updatedHistory[current].push(entered);

    const updatedResults = [...questionResults];
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    setStudentAnswers(updatedStudentAnswers);
    setWorkingAnswers(updatedWorkingAnswers);
    setAttemptHistory(updatedHistory);

    if (isCorrect) {
      updatedResults[current] = "correct";
      setQuestionResults(updatedResults);
      setFeedback(
        <div style={styles.statusLine}>
          <ResultIcon type="correct" />
          <strong style={{ color: "#087f5b" }}>Correct!</strong>
        </div>
      );
    } else if (newAttempts >= 3) {
      updatedResults[current] = "incorrect";
      setQuestionResults(updatedResults);
      setFeedback("Correct answer shown in blue. You can move on now.");
    } else {
      setFeedback(
        <div style={styles.statusLine}>
          <ResultIcon type="incorrect" />
          <strong style={{ color: "#c0392b" }}>
            Try again. Attempts left: {3 - newAttempts}
          </strong>
        </div>
      );
    }

    saveProgress({
      studentAnswers: updatedStudentAnswers,
      workingAnswers: updatedWorkingAnswers,
      attemptHistory: updatedHistory,
      questionResults: updatedResults,
      checkedTableProgress: updatedChecked,
    });
  };

  const canAdvance = questionResults[current] === "correct" || questionResults[current] === "incorrect";

  const updateOptionalTable = (values) => {
    const updated = { ...optionalTableProgress, [questions[current].id]: values };
    setOptionalTableProgress(updated);
    saveProgress({ optionalTableProgress: updated });
  };

  const submitResults = async (results, answers, histories, workings) => {
    setSubmissionError("");
    setSubmissionStatus("sending");
    saveProgress({ done: true, questionResults: results, studentAnswers: answers, attemptHistory: histories, workingAnswers: workings, submissionStatus: "sending" });

    const payload = {
      submissionId,
      studentName: studentName.trim(),
      quizTitle: "Volume of Cylinders",
      completedAt: new Date().toISOString(),
      score: results.filter((result) => result === "correct").length,
      total: questions.length,
      questions: questions.map((question, index) => ({
        number: index + 1,
        question: question.instruction,
        instruction: question.instruction,
        cylinder: {radius:question.radiusLabel,diameter:question.diameterLabel,height:question.heightLabel,base:question.base},
        formAnswers: optionalTableProgress[question.id] || {},
        correctAnswer: question.displayAnswer,
        studentAnswer: answers[index] || "No answer",
        attempts: histories[index] || [],
        working: workings[index] || "",
        result: results[index] || "incorrect",
      })),
    };

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const messages = {
          404: "The results service could not be found. Please let Ryan know.",
          503: "Email delivery is not configured yet. Please let Ryan know.",
          502: "The email service could not deliver your results. Please try again later.",
        };
        throw new Error(messages[response.status] || "The results service could not accept your results. Please try again later.");
      }
      setSubmissionStatus("sent");
      saveProgress({ done: true, submissionStatus: "sent", submissionError: "" });
    } catch (error) {
      const message = error instanceof TypeError
        ? "Cannot connect to the results service. Please let Ryan know or try again later."
        : error.message;
      setSubmissionError(message);
      setSubmissionStatus("failed");
      saveProgress({ done: true, submissionStatus: "failed", submissionError: message });
    }
  };

  const nextQuestion = () => {
    setFeedback(null);
    setAttempts(0);
    const next = current + 1;

    if (next < questions.length) {
      setCurrent(next);
      setStudentAnswer(studentAnswers[next] || "");
      setWorking(workingAnswers[next] || "");
      saveProgress({ current: next });
      return;
    }

    if (reviewMode) {
      navigate("/student-dashboard");
      return;
    }

    setDone(true);
    saveProgress({ done: true });
    if (submissionStatus !== "sent") {
      submitResults(questionResults, studentAnswers, attemptHistory, workingAnswers);
    }
  };

  const previousQuestion = () => {
    if (current === 0) return;
    const previous = current - 1;
    setCurrent(previous);
    setFeedback(null);
    setAttempts(attemptHistory[previous]?.length || 0);
    setStudentAnswer(studentAnswers[previous] || "");
    setWorking(workingAnswers[previous] || "");
    saveProgress({ current: previous });
  };

  const startReview = () => {
    setReviewMode(true);
    setCurrent(0);
    setFeedback(null);
    setStudentAnswer(studentAnswers[0] || "");
    setWorking(workingAnswers[0] || "");
  };

  const retrySubmission = () => submitResults(questionResults, studentAnswers, attemptHistory, workingAnswers);
  const score = questionResults.filter((result) => result === "correct").length;

  const feedbackMessage = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage === 100) return "🌟 Perfect—you got everything right!";
    if (percentage >= 80) return "🎉 Great job—you did really well!";
    if (percentage >= 50) return "👍 Good effort. Review any red questions.";
    return "💪 Keep practising. Reviewing your answers will help.";
  };

  const renderProgress = () => (
    <div style={styles.progressWrapper}>
      {questionResults.map((result, index) => (
        <div key={questions[index].id} style={{
          ...styles.progressDot,
          backgroundColor: result === "correct" ? "#00b894" : result === "incorrect" ? "#d63031" : "#636e72",
          border: index === current && !done ? "3px solid #f9b710" : "2px solid transparent",
        }}>
          {index + 1}
        </div>
      ))}
    </div>
  );

  if (!nameConfirmed) {
    return (
  
        <div style={styles.container}><style>{CYLINDER_REFERENCE_CSS}</style>
          <div style={styles.nameCard}>
            <h1 style={styles.heading}>Volume of Cylinders Quiz</h1>
            <p>Please enter your name before you begin. Your results will be sent to Ryan when you finish.</p>
            <form onSubmit={confirmName}>
              <label htmlFor="student-name" style={styles.label}>Your name</label>
              <input id="student-name" autoFocus autoComplete="name" maxLength={80} value={studentName}
                onChange={(event) => setStudentName(event.target.value)} style={styles.nameInput} />
              <button type="submit" disabled={!studentName.trim()} style={styles.startBtn}>Start Quiz ➡️</button>
            </form>
          </div>
        </div>
   
    );
  }

  const q = questions[current];
  const currentResult = questionResults[current];
  const completedFieldStyle =
    currentResult === "correct"
      ? styles.correctField
      : currentResult === "incorrect"
        ? styles.incorrectField
        : styles.unansweredField;

  return (
       <div style={styles.container}><style>{CYLINDER_REFERENCE_CSS}</style>
        <h1 style={styles.heading}>Volume of Cylinders</h1>
        <p style={styles.studentLabel}>Student: <strong>{studentName}</strong></p>
        {renderProgress()}

        {done && !reviewMode ? (
          <div style={styles.resultsBox}>
            <h2 style={{ fontFamily: "Inter, 'Segoe UI', sans-serif", color: "#17324d", fontWeight: 700 }}>Score: {score} / {questions.length}</h2>
            <p style={styles.feedbackText}>{feedbackMessage()}</p>
            {submissionStatus === "sending" && <p>Sending your results to Ryan…</p>}
            {submissionStatus === "sent" && (
              <div style={{ ...styles.statusLine, ...styles.sentMessage }}>
                <ResultIcon type="correct" size={25} />
                <span>Your results have been sent to Ryan.</span>
              </div>
            )}
            {submissionStatus === "failed" && (
              <div>
                <p style={styles.errorMessage}>Your quiz is saved, but the results could not be sent.</p>
                {submissionError && <p role="alert">{submissionError}</p>}
                <button onClick={retrySubmission} style={styles.submitBtn}>Try Sending Again</button>
              </div>
            )}
            <button onClick={startReview} style={styles.reviewBtn}>🔍 Review Questions</button>
          </div>
        ) : (
          <div style={styles.card}>
            <h3 style={styles.questionHeading}>Question {current + 1} of {questions.length}</h3>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 24 }}>
              <div style={{ ...styles.instruction, flex: 1, margin: 0 }}>
                {Number(q.id.slice(1))>=6
                  ? q.instruction.split("\n").map((line,index)=><p key={index} style={{margin:index===0?'0 0 14px':'14px 0 0'}}>{line}</p>)
                  : q.instruction.split("\n").map((line,index)=><React.Fragment key={index}>{index>0&&<br/>}{line}</React.Fragment>)}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                <CalculatorIcon allowed={q.calculator === "yes"} />
              </div>
            </div>
            <div className="cylinder-reference-row">
              <CylinderDiagram question={q}/>
              {q.pi&&<aside className={q.formula?'cylinder-formula-box':'cylinder-formula-box cylinder-pi-only'} aria-label={q.formula?'Cylinder formula and value of pi':'Value of pi to use'}>
                {q.formula&&<><p>V = Volume of cylinder</p><p>r = radius</p><p>h = height</p></>}
                <p className="cylinder-pi-value">{q.pi.includes('frac')?'π = 22/7':'π = 3.142'}</p>
              </aside>}
            </div>
            {q.base&&<CylinderDiagram question={q} base/>}
            <>
            {q.fields&&<CylinderOrderForm question={q} values={optionalTableProgress[q.id]||{}} readOnly={reviewMode||canAdvance} checkedValues={checkedTableProgress[q.id]} showAnswers={questionResults[current]==='incorrect'} onChange={updateOptionalTable}/>}

            {!q.formula&&<>
            <label style={styles.label}>Show your working:</label>
            <textarea rows={4} readOnly={reviewMode || canAdvance}
              value={reviewMode ? workingAnswers[current] : working}
              onChange={(event) => setWorking(event.target.value)}
              placeholder="Show any working here" style={{ ...styles.workingInput, ...completedFieldStyle }} />
            </>}

            {!q.fields&&<>
            <label style={styles.label}>Answer:</label>
            <div style={{display:"flex",alignItems:"center",gap:10}}>{q.unit==="£"&&<span>£</span>}<math-field readOnly={reviewMode || canAdvance} style={{ ...styles.mathInput, ...completedFieldStyle, ...(questionResults[current] === "incorrect" ? { color: "#1d4ed8", backgroundColor: "#eff6ff", borderColor: "#60a5fa" } : {}) }}
              value={questionResults[current] === "incorrect" ? q.displayAnswer : reviewMode || canAdvance ? studentAnswers[current] : studentAnswer}
              onInput={(event) => !reviewMode && setStudentAnswer(event.target.value)} />{q.unit&&q.unit!=="£"&&<span style={{fontSize:20,whiteSpace:"nowrap"}}>{q.unit}</span>}</div>
            </>}

              </>

            {reviewMode && (
              <div style={styles.reviewAnswer}>
                <div style={styles.statusLine}>
                  <ResultIcon type={questionResults[current] === "correct" ? "correct" : "incorrect"} />
                  <strong style={{ color: questionResults[current] === "correct" ? "#087f5b" : "#c0392b" }}>
                    {questionResults[current] === "correct" ? "Correct:" : "Correct answer:"}
                  </strong>
                </div>
                {q.fields?<p>{q.displayAnswer}</p>:<BlockMath math={q.displayAnswer} />}
              </div>
            )}
            {!reviewMode && feedback && <div style={styles.feedbackBox}>{feedback}</div>}
            {!reviewMode && !canAdvance && <button onClick={checkAnswer} style={styles.submitBtn}>Check Answer</button>}

            {(canAdvance || reviewMode) && (
              <div>
                {current > 0 && <button onClick={previousQuestion} style={styles.secondaryBtn}>← Back</button>}
                <button onClick={nextQuestion} style={styles.nextBtn}>
                  {current + 1 < questions.length ? "Next ➡️" : reviewMode ? "Finish Review" : "Finish & Send 🎉"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    
  );
}

const styles = {
  container: { padding: "2rem", fontFamily: "'Fredoka', cursive", backgroundColor: "#eaf4fc", minHeight: "100vh" },
  heading: { fontFamily: "Inter, 'Segoe UI', sans-serif", fontSize: "clamp(1.6rem, 3vw, 2.25rem)", fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.035em", color: "#17324d", textAlign: "center", maxWidth: 760, margin: "0 auto 1.5rem", paddingBottom: "1rem", borderBottom: "3px solid #b8d4e8" },
  questionHeading: { fontFamily: "Inter, 'Segoe UI', sans-serif", fontSize: "1rem", fontWeight: 600, letterSpacing: "0.025em", color: "#475569", margin: "0 0 1.25rem" },
  studentLabel: { textAlign: "center", marginBottom: "1rem" },
  card: { backgroundColor: "#fff", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", maxWidth: 700, margin: "1rem auto 0" },
  nameCard: { backgroundColor: "#fff", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", maxWidth: 520, margin: "4rem auto" },
  nameInput: { display: "block", width: "100%", boxSizing: "border-box", padding: "0.75rem", margin: "0.5rem 0 1rem", border: "1px solid #aaa", borderRadius: 8, fontSize: "1.1rem" },
  startBtn: { padding: "0.7rem 1.3rem", backgroundColor: "#6c5ce7", color: "white", fontWeight: "bold", border: 0, borderRadius: 7, cursor: "pointer" },
  calculatorIndicator: { marginBottom: 12, display: "flex", justifyContent: "flex-end" },
  instruction: { fontWeight: 700, fontSize: "1.05rem", marginBottom: 24 },
  label: { fontFamily: "Inter, 'Segoe UI', sans-serif", color: "#334155", fontWeight: 600, marginTop: 10, display: "block" },
  mathInput: { display: "block", width: "100%", boxSizing: "border-box", fontSize: "1.25rem", padding: "0.65rem", borderRadius: 8, border: "1px solid #ccc", marginTop: 8 },
  workingInput: { width: "100%", boxSizing: "border-box", borderRadius: 8, border: "1px solid #ccc", padding: "0.75rem", fontSize: "1rem", fontFamily: "'Fredoka', cursive", marginTop: 8, marginBottom: 20 },
  unansweredField: { backgroundColor: "#fff", border: "1px solid #ccc" },
  correctField: { backgroundColor: "#e8f8ee", border: "2px solid #42a96b" },
  incorrectField: { backgroundColor: "#fdeaea", border: "2px solid #d95858" },
  feedbackBox: { marginTop: "1rem" },
  statusLine: { display: "flex", alignItems: "center", gap: 10 },
  reviewAnswer: { marginTop: "1rem", padding: "1rem", backgroundColor: "#f5f6fa", borderRadius: 8 },
  submitBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#0984e3", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer", marginRight: "0.75rem" },
  nextBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#00b894", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer" },
  secondaryBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#636e72", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer", marginRight: "0.75rem" },
  progressWrapper: { display: "flex", justifyContent: "center", gap: 10, marginBottom: "1.5rem", flexWrap: "wrap" },
  progressDot: { width: 36, height: 36, borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center", fontWeight: "bold", color: "white" },
  resultsBox: { textAlign: "center", backgroundColor: "white", maxWidth: 700, margin: "2rem auto", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)" },
  feedbackText: { fontSize: "1.25rem", margin: "1rem 0" },
  sentMessage: { color: "#087f5b", fontWeight: 700, justifyContent: "center", margin: "1rem 0" },
  errorMessage: { color: "#c0392b", fontWeight: 700 },
  reviewBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#636e72", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer" },
};


function CalculatorIcon({ allowed }) {
  const label = allowed ? "Calculator allowed" : "No calculator";

  return (
    <svg role="img" aria-label={label} width={56} height={40} viewBox="0 0 56 40"
      style={{ display: "block" }}>
      <title>{label}</title>
      <rect x="5" y="3" width="27" height="34" rx="4" fill="#f1f5f9" stroke="#334155" strokeWidth="2" />
      <rect x="10" y="8" width="17" height="7" rx="1" fill="#cbd5e1" stroke="#334155" />
      <path d="M11 21h2m5 0h2m5 0h2M11 27h2m5 0h2m5 0h2M11 32h2m5 0h2m5 0h2"
        stroke="#334155" strokeWidth="3" strokeLinecap="round" />
      {allowed ? (
        <path d="M37 22l5 5 9-12" fill="none" stroke="#138a5b" strokeWidth="4"
          strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M5 5l27 30M32 5L5 35" fill="none" stroke="#d64545" strokeWidth="4"
          strokeLinecap="round" />
      )}
    </svg>
  );
}


function numericValue(raw){
  const value=normalize(raw).replace(/,/g,'');
  if(/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(value))return Number(value);
  const fraction=value.match(/^([+-]?\d+)\/(\d+)$/);
  return fraction&&Number(fraction[2])!==0?Number(fraction[1])/Number(fraction[2]):NaN;
}
function matches(value,expected,range){
  if(typeof expected!=='number')return String(value)===String(expected);
  const n=numericValue(value);if(!Number.isFinite(n))return false;
  if(!range)return Math.abs(n-expected)<1e-8;
  const scale=Number.isInteger(expected)?1:1000;
  return n>=range[0]-1e-8&&n<=range[1]+1e-8&&Math.abs(n*scale-Math.round(n*scale))<1e-7;
}
function matchesCylinderAnswer(q,raw){
  if(q.exactVolume===undefined)return q.answers.some(answer=>matches(raw,Number(answer),q.range));
  const value=numericValue(raw),expected=q.exactVolume;
  if(!Number.isFinite(value))return false;
  if(Math.abs(value-expected)<1e-8)return true;
  const decimal=normalize(raw).replace(/,/g,'').match(/^\d+\.(\d+)$/);
  if(!decimal)return false;
  const scale=10**Math.min(decimal[1].length,10);
  const rounded=Math.round((expected+1e-10)*scale)/scale;
  const truncated=Math.floor((expected+1e-10)*scale)/scale;
  return Math.abs(value-rounded)<1e-8||Math.abs(value-truncated)<1e-8;
}
function fieldSummary(q,values){return q.fields.map(f=>`${f.label}: ${f.unit==='£'?'£':''}${values[f.key]??''}${f.unit&&f.unit!=='£'?' '+f.unit:''}`).join('; ');}

function CylinderDiagram({question:q}){
  const id=`cylinder-${q.id}`,a=q.appearance;
  const palettes={teal:['#e0faf3','#7dd3c7','#289b91'],blue:['#e0f2fe','#7db8ec','#3267b0'],violet:['#f1e9ff','#b7a0e5','#8055ba'],rose:['#ffe5eb','#eca0b5','#b84f78'],amber:['#fff1ce','#ebc06c','#b58230'],green:['#e5f7df','#a2ce8d','#519157'],earth:['#f3e6d6','#c9a276','#927049']};
  const colours=palettes[a.colour],rx=a.radius,top=65,bottom=top+a.bodyHeight,cx=180,ry=rx*.27,horizontal=a.orientation==='horizontal';
  const arrow={markerStart:`url(#${id}-arrow)`,markerEnd:`url(#${id}-arrow)`,stroke:'#17324d',strokeWidth:1.5};
  return <figure style={{margin:'12px auto 24px',maxWidth:440}}>
    <svg viewBox={`0 0 460 ${horizontal?340:bottom+(a.orientation==='tilted'?115:90)}`} role="img" aria-labelledby={`${id}-title`} data-orientation={a.orientation} data-colour={a.colour} style={{width:'100%',display:'block',fontFamily:"Inter, 'Segoe UI', sans-serif"}}>
      <title id={`${id}-title`}>Cylinder: {q.diameterLabel?'diameter '+q.diameterLabel:'radius '+q.radiusLabel}, height {q.heightLabel}</title>
      <defs><linearGradient id={`${id}-shade`}><stop stopColor={colours[1]}/><stop offset=".4" stopColor={colours[0]}/><stop offset="1" stopColor={colours[2]}/></linearGradient><marker id={`${id}-arrow`} markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto-start-reverse"><path d="M0 0L7 3.5L0 7" fill="#17324d"/></marker></defs>
      {horizontal?<g>
        <path d={`M110 ${155-rx}H330V${155+rx}H110Z`} fill={`url(#${id}-shade)`} stroke={colours[2]} strokeWidth="2"/>
        <ellipse cx="110" cy="155" rx="22" ry={rx} fill={colours[1]} stroke={colours[2]} strokeWidth="2"/>
        <ellipse cx="330" cy="155" rx="22" ry={rx} fill={colours[0]} stroke={colours[2]} strokeWidth="2"/>
        <circle cx="110" cy="155" r="3" fill="#17324d"/>
        <line x1="110" y1={q.diameterLabel?155-rx:155} x2="110" y2={155+rx} stroke="#17324d" strokeDasharray={q.diameterLabel?'5 3':undefined} strokeWidth="2"/>
        <line x1="65" y1={q.diameterLabel?155-rx:155} x2="65" y2={155+rx} {...arrow}/>
        <text x="44" y={155+(q.diameterLabel?0:rx/2)} textAnchor="middle" transform={`rotate(-90 44 ${155+(q.diameterLabel?0:rx/2)})`} fontSize="17" fill="#17324d">{q.diameterLabel||q.radiusLabel}</text>
        <line x1="110" y1={155+rx+30} x2="330" y2={155+rx+30} {...arrow}/>
        <text x="220" y={155+rx+56} textAnchor="middle" fontSize="17" fill="#17324d">{q.heightLabel}</text>
      </g>:<g transform={a.orientation==='tilted'?`rotate(${a.angle} 220 155)`:undefined}>
        <path d={`M${cx-rx} ${top}V${bottom}A${rx} ${ry} 0 0 0 ${cx+rx} ${bottom}V${top}`} fill={`url(#${id}-shade)`} stroke={colours[2]} strokeWidth="2"/>
        <path d={`M${cx-rx} ${bottom}A${rx} ${ry} 0 0 1 ${cx+rx} ${bottom}`} fill="none" stroke={colours[2]} strokeDasharray="5 5"/>
        {q.fill&&<path d={`M${cx-rx+1} ${bottom-a.bodyHeight*q.fill}Q${cx} ${bottom-a.bodyHeight*q.fill+ry*2} ${cx+rx-1} ${bottom-a.bodyHeight*q.fill}V${bottom}Q${cx} ${bottom+ry*2} ${cx-rx+1} ${bottom}Z`} fill="#278dd3" opacity=".5"/>}
        <ellipse cx={cx} cy={top} rx={rx} ry={ry} fill={colours[0]} stroke={colours[2]} strokeWidth="2"/>
        <circle cx={cx} cy={top} r="3" fill="#17324d"/>
        <line x1={q.diameterLabel?cx-rx:cx} y1={top} x2={cx+rx} y2={top} stroke="#17324d" strokeWidth="2" strokeDasharray={q.diameterLabel?'5 3':undefined}/>
        <text x={q.diameterLabel?cx:cx+rx/2} y={top-ry-12} textAnchor="middle" fontSize="17" fill="#17324d">{q.diameterLabel||q.radiusLabel}</text>
        <line x1={cx+rx+30} y1={top} x2={cx+rx+30} y2={bottom} {...arrow}/>
        <text x={cx+rx+46} y={(top+bottom)/2+6} fontSize="16" fill="#17324d">{q.heightLabel}</text>
      </g>}
      <text x="220" y={horizontal?325:bottom+(a.orientation==='tilted'?100:75)} textAnchor="middle" fontSize="13" fill="#64748b">{q.diagramCaption||'Not to scale'}</text>
    </svg>
  </figure>;
}

function CylinderOrderForm({question:q,values,readOnly,checkedValues,showAnswers,onChange}){
  const blank=key=>{
    const f=q.fields.find(field=>field.key===key),value=showAnswers?f.expected:(values[key]??'');
    const colour=showAnswers?{backgroundColor:'#eff6ff',borderColor:'#60a5fa',color:'#1d4ed8'}:checkedValues?(matches(checkedValues[key],f.expected,f.range)?guidedStyles.correct:guidedStyles.incorrect):{};
    const controlStyle={...guidedStyles.input,width:f.options?'100%':100,...colour};
    return <label style={{display:'inline-flex',alignItems:'center',gap:6,maxWidth:'100%'}}>{f.unit==='£'&&<span>£</span>}{f.options?<select aria-label={f.label} value={value} disabled={readOnly} onChange={e=>onChange({...values,[key]:e.target.value})} style={{...controlStyle,fontSize:15}}><option value="">Choose a reason</option>{f.options.map(([v,label])=><option key={v} value={v}>{label}</option>)}</select>:<input aria-label={f.label} inputMode="decimal" value={value} readOnly={readOnly} onChange={e=>onChange({...values,[key]:e.target.value})} style={controlStyle}/>}{f.unit&&f.unit!=='£'&&<span>{f.unit}</span>}</label>;
  };
  const cell={...guidedStyles.cell,padding:'10px 8px',fontSize:'1rem'};
  return <section style={{marginBottom:24}}>
    <div style={{overflowX:'auto'}}><table style={guidedStyles.table} aria-label={q.id==='q10'?q.orderTitle:'Soil order calculation'}><thead><tr><th style={cell}>Quantity</th><th style={cell}>Your answer</th></tr></thead><tbody>{q.fields.filter(f=>q.id==='q9'||['volume','bags'].includes(f.key)).map(f=><tr key={f.key}><th scope="row" style={cell}>{f.label}</th><td style={cell}>{blank(f.key)}</td></tr>)}</tbody></table></div>
    {q.id==='q10'&&<><h4>{q.orderTitle}</h4><div style={{overflowX:'auto'}}><table style={{...guidedStyles.table,minWidth:540}} aria-label="Pack quantities and costs"><thead><tr>{['Pack size','Price per pack','Number of packs','Cost'].map(h=><th style={cell} key={h}>{h}</th>)}</tr></thead><tbody>{q.packOffers.map(([n,price])=><tr key={n}><th scope="row" style={cell}>Pack of {n}</th><td style={cell}>£{price}</td><td style={cell}>{blank(`pack${n}`)}</td><td style={cell}>{blank(`cost${n}`)}</td></tr>)}</tbody><tfoot><tr><th colSpan={3} style={cell}>Total cost</th><td style={cell}>{blank('total')}</td></tr></tfoot></table></div></>}
  </section>;
}

const guidedStyles = {
  stepHeading: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 },
  hintButton: { display: "grid", placeItems: "center", padding: 0, boxSizing: "border-box", flexShrink: 0, width: 40, height: 40, borderRadius: "50%", border: "2px solid #f59e0b", cursor: "pointer", boxShadow: "0 3px 0 #fcd34d" },
  hintDialog: { border: 0, padding: 36, background: "transparent", maxWidth: 540, width: "calc(100% - 24px)", boxSizing: "border-box", overflow: "visible", fontFamily: "'Fredoka', cursive" },
  hintBubble: { position: "relative", padding: 28, border: "3px solid #7c3aed", borderRadius: "32px 32px 32px 8px", background: "#fffbeb", color: "#334155", boxShadow: "0 0 20px #c4b5fd, 0 0 44px rgba(167,139,250,0.7), 0 12px 0 rgba(109,40,217,0.18)" },
  bubbleTail: { position: "absolute", bottom: -17, left: 28, width: 26, height: 26, background: "#fffbeb", borderRight: "3px solid #7c3aed", borderBottom: "3px solid #7c3aed", transform: "rotate(45deg)" },
  section: { borderWidth: 2, borderStyle: "solid", borderColor: "#cbd5e1", borderRadius: 8, padding: 16, marginBottom: 16 },
  correct: { backgroundColor: "#e8f8ee", borderColor: "#42a96b" },
  incorrect: { backgroundColor: "#fdeaea", borderColor: "#d95858" },
  table: { borderCollapse: "collapse", width: "100%", border: "2px solid #334155", backgroundColor: "white", fontSize: "1.15rem" },
  cell: { border: "1px solid #334155", padding: "14px 12px", textAlign: "center" },
  input: { width: 70, maxWidth: "100%", boxSizing: "border-box", padding: 8, borderWidth: 2, borderStyle: "solid", borderColor: "#94a3b8", borderRadius: 5, fontSize: "1.1rem", textAlign: "center" },
  button: { display: "block", marginTop: 16, padding: "10px 16px", backgroundColor: "#0984e3", color: "white", border: 0, borderRadius: 6, cursor: "pointer", fontWeight: "bold" },
  success: { color: "#087f5b", fontWeight: "bold" },
};


// Generate short feedback tones locally, without loading audio files.
let quizAudioContext;
async function playQuizFeedback(correct) {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    if (!quizAudioContext || quizAudioContext.state === "closed") quizAudioContext = new AudioContextClass();
    if (quizAudioContext.state === "suspended") await quizAudioContext.resume();
    const context = quizAudioContext;
    const start = context.currentTime;
    const oscillator = context.createOscillator();
    const volume = context.createGain();
    oscillator.type = correct ? "sine" : "triangle";
    oscillator.frequency.setValueAtTime(correct ? 880 : 240, start);
    if (correct) oscillator.frequency.setValueAtTime(1174.66, start + 0.07);
    else {
      oscillator.frequency.exponentialRampToValueAtTime(170, start + 0.1);
      oscillator.frequency.setValueAtTime(210, start + 0.14);
      oscillator.frequency.exponentialRampToValueAtTime(130, start + 0.28);
    }
    const duration = correct ? 0.2 : 0.3;
    volume.gain.setValueAtTime(0.0001, start);
    volume.gain.exponentialRampToValueAtTime(correct ? 0.15 : 0.14, start + 0.008);
    if (!correct) {
      volume.gain.exponentialRampToValueAtTime(0.0001, start + 0.11);
      volume.gain.setValueAtTime(0.0001, start + 0.14);
      volume.gain.exponentialRampToValueAtTime(0.14, start + 0.15);
    }
    volume.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(volume);
    volume.connect(context.destination);
    oscillator.onended = () => { oscillator.disconnect(); volume.disconnect(); };
    oscillator.start(start);
    oscillator.stop(start + duration + 0.01);
  } catch {
    // Audio availability should never interrupt answer checking.
  }
}

const CYLINDER_REFERENCE_CSS=`
.cylinder-reference-row{display:flex;align-items:center;justify-content:center;gap:20px;flex-wrap:wrap;margin:16px 0 24px}
.cylinder-reference-row figure{flex:1 1 280px;max-width:390px;margin:0!important}
.cylinder-formula-box{flex:0 1 250px;padding:18px 22px;border:2px solid #7babbf;border-radius:12px;background:#eff8ff;color:#17324d;text-align:left;box-sizing:border-box}
.cylinder-formula-box h4{margin:0 0 12px;font-size:17px}
.cylinder-formula-box p{margin:10px 0;font-size:17px;line-height:1.45}
.cylinder-formula-box .cylinder-pi-value{font-size:21px;font-weight:600;white-space:nowrap}
.cylinder-formula-box.cylinder-pi-only{padding:22px 26px;flex-basis:230px}
.cylinder-pi-only .cylinder-pi-value{font-size:28px;margin:0}
.cylinder-formula-box .katex-display{margin:12px 0;text-align:center;font-size:1.12em}
.cylinder-pi{margin-top:16px;padding-top:12px;border-top:1px solid #bdd6e4}
.cylinder-formula-box>.cylinder-pi:first-child{border:0;margin:0;padding:0}
@media(max-width:600px){.cylinder-reference-row{gap:12px}.cylinder-reference-row figure{flex-basis:100%}.cylinder-formula-box{flex-basis:100%;max-width:300px}}
`;
